import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck } from 'lucide-react';

export const enquiryUrl = (category, type, extra) => {
  const params = new URLSearchParams();
  if (category) params.set('category', category.slug);
  if (type) params.set('type', type);
  if (category && category.export_controlled) params.set('enquiry', 'advanced-compute');
  if (extra) Object.keys(extra).forEach((k) => params.set(k, extra[k]));
  return `/contact?${params.toString()}#enquiry-form`;
};

export const ExportBadge = (props) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-slate-300 bg-slate-50 text-slate-600 font-manrope text-[10px] font-semibold tracking-wide ${props.className || ''}`} data-testid={props.testId || 'export-notice-badge'}>
    <ShieldCheck className="w-3 h-3" /> Export controls may apply
  </span>
);

// Category card for the overview grid
export const CategoryCard = ({ category, index, onSelect }) => {
  const consumer = category.slug === 'consumer-technology';
  return (
    <button
      type="button"
      onClick={() => onSelect(category.slug)}
      className={`text-left rounded-3xl border overflow-hidden group transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_rgba(15,23,42,0.08)] ${consumer ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200/80 hover:border-[#0096D6]/40'}`}
      data-testid={`category-card-${category.slug}`}
    >
      <div className="aspect-[4/3] bg-white border-b border-slate-100 p-5 overflow-hidden relative">
        <img src={category.image} alt={category.name} loading={index < 4 ? 'eager' : 'lazy'} decoding="async" className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute top-3 left-3 font-space text-[10px] font-semibold tracking-widest text-slate-400">{String(index + 1).padStart(2, '0')}</span>
        {category.export_controlled && <ExportBadge className="absolute top-3 right-3" testId={`category-export-badge-${category.slug}`} />}
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <h3 className="font-outfit text-lg font-semibold text-slate-900">{category.name}</h3>
          {consumer && <span className="px-2 py-0.5 rounded-full border border-slate-200 text-slate-400 font-manrope text-[9px] font-semibold tracking-[0.14em] uppercase whitespace-nowrap">Secondary</span>}
        </div>
        <p className="font-manrope text-sm text-slate-600 leading-relaxed line-clamp-2 mb-3">{category.description}</p>
        <div className="flex items-center justify-between">
          <span className="font-manrope text-xs text-slate-500" data-testid={`category-count-${category.slug}`}>
            {category.listed_count > 0 ? `${category.listed_count} listed product${category.listed_count === 1 ? '' : 's'}` : 'Sourced to order'}
          </span>
          <span className="inline-flex items-center gap-1 font-manrope text-sm font-semibold text-[#0096D6]">
            Open <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </button>
  );
};

// Purpose-built state for categories with no currently listed stock — never substitutes unrelated products
export const SourcingState = ({ category, type }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-8 md:p-12 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center" data-testid="sourcing-state">
    <div>
      <span className="inline-block px-3 py-1 rounded-full bg-[#0096D6]/10 text-[#0096D6] font-manrope text-[11px] font-semibold tracking-[0.14em] uppercase mb-4">Sourcing Capability</span>
      <h3 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900 mb-3">Looking for a specific configuration?</h3>
      <p className="font-manrope text-slate-600 leading-relaxed max-w-2xl mb-2">
        Our high-performance hardware inventory changes frequently. Submit your requirements for current availability, quantity, location and lead time.
      </p>
      <p className="font-manrope text-sm text-slate-500 leading-relaxed max-w-2xl">{category.sourcing}</p>
      {category.export_controlled && (
        <p className="font-manrope text-xs text-slate-500 mt-4 flex items-start gap-2"><ShieldCheck className="w-4 h-4 text-[#0096D6] shrink-0 mt-0.5" />{category.export_notice}</p>
      )}
    </div>
    <div className="flex flex-col gap-3 min-w-[240px]">
      <Link to={enquiryUrl(category, type)} className="btn-primary inline-flex items-center justify-center gap-2 whitespace-nowrap" data-testid="request-availability-btn">
        Request Availability <ArrowRight className="w-4 h-4" />
      </Link>
      <Link to={enquiryUrl(category, type, { requirements: '1' })} className="btn-secondary inline-flex items-center justify-center gap-2 whitespace-nowrap" data-testid="send-requirements-btn">
        Send Your Requirements
      </Link>
    </div>
  </div>
);

// Listed product card (existing consumer catalogue records, untouched)
export const ProductCard = ({ product, category }) => (
  <div className="group opulent-card overflow-hidden" data-testid={`product-card-${product.id}`}>
    <div className="aspect-square overflow-hidden relative">
      <img src={product.image} alt={product.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-4">
        <Link to={enquiryUrl(category, null, { product: product.name })} className="btn-primary text-sm py-2 px-4 w-full text-center" data-testid={`inquire-btn-${product.id}`}>
          Request Quote
        </Link>
      </div>
    </div>
    <div className="p-5">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-block px-2 py-1 rounded-full bg-[#0096D6]/10 text-[#0096D6] font-manrope text-xs font-medium">{product.subcategory || product.category}</span>
        {product.export_controlled && <ExportBadge testId={`product-export-badge-${product.id}`} />}
      </div>
      <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-2 group-hover:text-[#0096D6] transition-colors duration-300">{product.name}</h3>
      <p className="font-manrope text-slate-500 text-sm mb-3 line-clamp-2">{product.description}</p>
      <div className="flex justify-between items-center text-sm">
        <span className="font-space text-slate-600">Min: {product.min_order}</span>
        <span className="text-[#0096D6] font-medium flex items-center gap-1">Details <ArrowRight className="w-3 h-3" /></span>
      </div>
    </div>
  </div>
);
